import { type CSSProperties, type ReactNode, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ApiError } from "../api/client";
import { useLive } from "../live/channel";
import type { LivePayload } from "../live/types";
import { Activity } from "../live/Activity";
import { Wall } from "../live/Wall";
import { RoomBox } from "../live/roomBox";
import { fitScale, type Viewport } from "../live/roomLayout";
import { PAIR_PATH } from "../live/pairCode";
import { useEvent } from "../context/EventProvider";
import { useT, useText } from "../settings/context";
import { gradientRefusal } from "../theme/palette";
import { DEFAULT_PAGE, usePageGradient, usePageImage } from "../theme/useTheme";
import { fallbackOf } from "../settings/catalog";
import { useMode } from "../panel/mode";
import { Band } from "../site/Band";
import { Prose } from "../ui/Prose";

/**
 * ═══ L'ÉCRAN DE SALLE ═══
 *
 * Le vidéoprojecteur. Il ne décide de rien et ne se règle plus : il montre
 * l'activité en cours, la même que les téléphones, en grand et sans la vidéo. Les
 * quatre adresses de la V1 (`/live/orga`, `/live/speaker`, `/live/emotions`,
 * `/leaderboard`) tiennent dans celle-ci.
 *
 * ⚠️ IL S'AUTHENTIFIE PAR UN JETON D'ÉCRAN, pas par un compte. Personne ne se
 * connecte sur une machine au fond d'une salle, et un compte partagé serait un mot
 * de passe partagé. Le jeton est lié à UN événement, expire seul et se révoque sans
 * toucher à personne d'autre. Il remplace `bww7jzu29fs5zfuhi80v`, la chaîne écrite
 * en clair dans le bundle de la V1 qui ouvre le direct de n'importe quel événement
 * à qui lit la source.
 *
 * ⚠️ ET IL SE RANGE, parce qu'un projecteur redémarre. Reçu dans l'adresse, il est
 * gardé puis retiré de la barre : le laisser afficher un jeton en clair sur un
 * écran de dix mètres de large serait le publier.
 *
 * Displayed strings go through `t()`, French kept at the call site as the
 * fallback — EXCEPT the messages addressed to the ORGANISER (an empty catalog,
 * a step removed from the thread), French like the rest of the panel.
 */

/**
 * ⚠️ UNE CLÉ PAR ÉVÉNEMENT. Elle était unique pour tout le navigateur, alors que le
 * jeton, lui, ne vaut que pour UN événement : ouvrir l'écran de salle du second
 * écrasait celui du premier, et rouvrir le premier lui envoyait le jeton du second,
 * refusé. Sur le portable qui sert deux salles dans la journée, c'est le cas normal.
 */
const storeKeyOf = (domainId: number, eventId: number) =>
  `gk-live-screen:${domainId}:${eventId}`;

function useScreenToken(domainId: number, eventId: number): string | null {
  const [params, setParams] = useSearchParams();
  const fromUrl = params.get("screen");
  const key = storeKeyOf(domainId, eventId);

  const [stored, setStored] = useState<string | null>(null);

  /* Relu quand l'événement change : le composant n'est pas remonté entre deux. */
  useEffect(() => {
    try {
      setStored(localStorage.getItem(key));
    } catch {
      setStored(null);
    }
  }, [key]);

  useEffect(() => {
    if (!fromUrl) return;
    try {
      localStorage.setItem(key, fromUrl);
    } catch {
      /* stockage indisponible : le jeton ne survivra pas au redémarrage */
    }
    setStored(fromUrl);

    // Retiré de la barre d'adresse aussitôt : voir l'avertissement du fichier.
    const next = new URLSearchParams(params);
    next.delete("screen");
    setParams(next, { replace: true });
  }, [fromUrl, params, setParams, key]);

  return fromUrl || stored;
}

export function LiveScene() {
  const t = useT();
  const event = useEvent();
  const organiser = useMode() === "organiser";
  const token = useScreenToken(event?.domainId ?? 0, event?.eventId ?? 0);
  const { data: live, error, reactions } = useLive({
    screenToken: token,
    enabled: !!token,
  });

  if (!token) {
    /**
     * ⚠️ L'ORGANISATEUR VIENT ICI EXPRÈS, DEPUIS LE SÉLECTEUR D'ÉCRANS, pour régler
     * le contenu de l'attente et personnaliser l'apparence de l'écran. Sans jeton sur
     * ce navigateur, il ne trouvait que ce message : rien à survoler, rien à viser, et
     * l'écran de salle était le seul qu'on ne pouvait pas habiller. Il voit donc l'écran
     * d'attente, celui que la salle affiche entre deux activités.
     *
     * ⚠️ SANS LIRE LE DIRECT AVEC SA SESSION. Elle lui donnerait la charge de la RÉGIE —
     * les questions pas encore modérées comprises —, donc un écran que la salle ne
     * verra jamais. L'activité en cours se prévisualise avec un vrai jeton d'écran.
     */
    if (organiser) {
      return (
        <SceneFrame>
          <SceneView live={undefined} token={null} />
        </SceneFrame>
      );
    }
    return (
      <section className="page">
        <p className="empty">
          Cet écran de salle s'ouvre avec le code de l'événement :{" "}
          <a href={PAIR_PATH}>tapez-le ici</a>. Il est affiché sur la régie.
        </p>
      </section>
    );
  }

  /**
   * ⚠️ LA DERNIÈRE IMAGE VAUT MIEUX QUE LA MEILLEURE DES EXPLICATIONS. Cet écran
   * n'a personne devant lui pour lire un message : il est au fond d'une salle, et
   * ce qu'on lui demande est de continuer à montrer le direct. Tant qu'il a une
   * charge en cache, une lecture ratée ne l'efface pas.
   *
   * ⚠️ ET LE JETON N'EST ACCUSÉ QUE SI LE SERVEUR L'A REFUSÉ. Toute erreur
   * affichait « ce jeton n'est plus valable » : un 502 sur la relecture de trente
   * secondes suffisait à faire révoquer et réémettre un jeton qui n'avait rien, et
   * comme il est rangé haché, l'ancien était perdu pour rien.
   */
  const refused = error instanceof ApiError && (error.status === 401 || error.status === 403);

  /* Un refus, LUI, efface l'image : révoquer un écran doit l'éteindre, sinon le
     projecteur révoqué continuerait d'afficher le direct sur son cache. */
  if (refused) {
    return (
      <section className="page">
        <p className="empty">
          {t(
            "SCREEN_CODE_INVALID",
            "Le code de cet écran a été révoqué ou remplacé : tapez le code actuel sur la page /ecran.",
          )}{" "}
          <a href={PAIR_PATH}>/ecran</a>
        </p>
      </section>
    );
  }

  if (error && !live) {
    return (
      <section className="page">
        <p className="empty">
          {t(
            "LIVE_UNREACHABLE_SCREEN",
            "Le direct est momentanément injoignable. Cet écran réessaie tout seul.",
          )}
        </p>
      </section>
    );
  }

  return (
    <SceneFrame>
      <SceneView live={live} token={token} />
      {/* Les réactions passent PAR-DESSUS ce qui est à l'écran, quel que soit
          l'état de la diffusion : elles accompagnent, elles ne remplacent pas. */}
      <Wall reactions={reactions} />
    </SceneFrame>
  );
}

/**
 * ═══ LE MUR, ET SES DEUX BANDEAUX ═══
 *
 * Une image en haut, une image en bas, comme le site en porte — mais PAS les siennes :
 * le mode nu retire les bandeaux du site de cet écran, exprès (voir `Shell`). Ceux-ci
 * ont leurs propres réglages, famille « Bandeaux de l'écran de salle ».
 *
 * ⚠️ AUTOUR DE TOUT CE QUE LA SALLE AFFICHE, attente, activité et message de fin
 * confondus : un habillage qui disparaîtrait au lancement d'un sondage se lirait comme
 * un écran qui saute.
 *
 * ⚠️ À CÔTÉ DE LA SCÈNE, PAS DEDANS. Posés dans `.page.scene`, ils héritaient de sa
 * marge colorée et devaient la traverser en négatif — or cette marge n'est pas celle
 * que `live.css` écrit : `desktop.css`, chargée après, la redéfinit pour toutes les
 * pages. Vu à 1920 px : 19 px de bandeau hors de l'écran de chaque côté. Frères de la
 * scène dans la colonne qui défile, ils vont d'un bord à l'autre sans rien compter,
 * et la scène prend la hauteur qui reste (`flex` dans `live.css`).
 *
 * ⚠️ ILS GARDENT LEUR HAUTEUR, ET C'EST LE MILIEU QUI S'ADAPTE. Une première version
 * les faisait rétrécir quand la place manquait : l'organisateur réglait 180 px et en
 * voyait 162 sur un mur en 1080, 80 sur un 720. L'image est la sienne, faite à cette
 * hauteur ; ce qui se redimensionne, c'est ce qui est projeté entre les deux. D'où
 * `.scene__room`, un CONTENEUR : les tailles de la scène y sont en unités de conteneur
 * (`cqmin`, `cqh`) et plus de fenêtre, et `roomLayout` reçoit sa mesure (`RoomBox`)
 * au lieu de celle de la fenêtre. Sans bandeau, conteneur et fenêtre coïncident : rien
 * ne change.
 *
 * ⚠️ `hidden` PLUTÔT QU'ABSENT, comme les bandeaux du site : le bloc nommé reste dans
 * le DOM, et c'est lui que le panneau surligne.
 */
function SceneFrame({ children }: { children: ReactNode }) {
  const [room, setRoom] = useState<HTMLDivElement | null>(null);
  /**
   * ⚠️ LE FOND DU SITE QUAND IL A UN DÉCOR — un dégradé, une image —, LA MARQUE SINON.
   * Le mur porte la couleur principale, et c'est voulu (voir `.page.scene` dans
   * `live.css`) : une carte blanche sur un fond de page uni se lisait comme une page web
   * oubliée sur l'écran. Un décor, lui, l'organisateur qui l'a réglé pour son site le
   * veut aussi sur son mur, et le texte y est déjà mesuré contre lui.
   */
  const page = useText("FO_MEETING_V2_PAGE_COLOR", DEFAULT_PAGE);
  const gradient = usePageGradient();
  const image = usePageImage();
  const siteBackground = !!image || (!!gradient && !gradientRefusal(page, gradient));
  const [box, setBox] = useState<Viewport | null>(null);

  /* Le nœud en état, pas en `ref` : même raison que dans `site/Band.tsx`. */
  useEffect(() => {
    if (!room) return;
    const watch = new ResizeObserver((entries) => {
      const measured = entries[0]?.contentRect;
      if (!measured) return;
      const { width, height } = measured;
      setBox((was) =>
        was && was.width === width && was.height === height
          ? was
          : { width, height },
      );
    });
    watch.observe(room);
    return () => watch.disconnect();
  }, [room]);

  return (
    <>
      <Band
        imageKey="FO_MEETING_V2_LIVE_SCENE_HEADER_IMAGE"
        safeKey="FO_MEETING_V2_LIVE_SCENE_HEADER_IMAGE_SAFE"
        sidesKey="FO_MEETING_V2_LIVE_SCENE_HEADER_IMAGE_SIDES"
        heightKey="FO_MEETING_V2_LIVE_SCENE_HEADER_IMAGE_HEIGHT"
        cfg="sceneHeaderImage"
        className="band--scene"
      />
      <div ref={setRoom} className="scene__room">
        <RoomBox.Provider value={box}>
          <section className="page scene" data-bg={siteBackground ? "site" : undefined}>
            {children}
          </section>
        </RoomBox.Provider>
      </div>
      <Band
        imageKey="FO_MEETING_V2_LIVE_SCENE_FOOTER_IMAGE"
        safeKey="FO_MEETING_V2_LIVE_SCENE_FOOTER_IMAGE_SAFE"
        sidesKey="FO_MEETING_V2_LIVE_SCENE_FOOTER_IMAGE_SIDES"
        heightKey="FO_MEETING_V2_LIVE_SCENE_FOOTER_IMAGE_HEIGHT"
        cfg="sceneFooterImage"
        className="band--scene"
      />
    </>
  );
}

/**
 * ═══ CE QUE LA SALLE AFFICHE, SANS SON CADRE ═══
 *
 * Extrait pour que la RÉGIE puisse le montrer aussi : sans lui, l'organisateur
 * changeait « Ce que la salle affiche » et ne voyait rien bouger, puisque son
 * aperçu montrait le téléphone d'un participant. Une commande dont l'effet est
 * invisible se lit comme une commande qui ne marche pas.
 *
 * ⚠️ LE MÊME COMPOSANT DES DEUX CÔTÉS, pour la raison habituelle : une copie
 * « pour l'aperçu » diverge, et la divergence se voit un jour de plénière.
 */
/**
 * ═══ CE QUE LA SALLE AFFICHE ═══
 *
 * ⚠️ ELLE SUIT L'ACTIVITÉ, ELLE NE SE RÈGLE PLUS. Il y avait un sélecteur à quatre
 * valeurs dans la régie (l'activité, le classement, les réactions, rien) ; retiré au
 * tri du 2026-08-12. La salle montre ce que montrent les téléphones, et l'habillage
 * d'attente quand il n'y a rien : un état de moins à tenir, et une commande de moins
 * à comprendre.
 *
 * ⚠️ CE QUE ÇA A COÛTÉ, ET COMMENT C'EST RENDU. Le tri avait retiré au podium et au
 * mur de réactions leur seul déclencheur. Les deux sont revenus autrement, sans
 * réintroduire de sélecteur : le mur de réactions est une COUCHE qui passe par-dessus
 * ce qui est à l'écran (`live/Wall.tsx`), et le classement est un TYPE D'ACTIVITÉ, que
 * la régie lance comme un sondage (`live/Podium.tsx`).
 */
function SceneView({
  live,
  token,
}: {
  live: LivePayload | undefined;
  /** Porté jusqu'au classement, qui se relit par sa propre route authentifiée. */
  token: string | null;
}) {
  const afterHtml = useText(
    "FO_MEETING_V2_LIVE_AFTER",
    fallbackOf("FO_MEETING_V2_LIVE_AFTER"),
  );
  const waitingHtml = useText("FO_MEETING_V2_LIVE_SCENE_WAITING");

  /**
   * ⚠️ LA SALLE SUIT LA DIFFUSION, COMME LES TÉLÉPHONES. Elle ne regardait que
   * l'activité : appuyer sur « Terminée » basculait les téléphones sur le message
   * de fin et laissait le mur afficher le dernier quiz, résultats compris, pendant
   * que la salle se vidait. Symétriquement, une activité répétée avant l'ouverture
   * s'affichait sur dix mètres alors qu'aucun téléphone ne la voyait.
   */
  if (live && live.state.broadcast === "ended") {
    return (
      /* ⚠️ PAS `se-prose` ICI. Ce décor est fait pour un bloc de page : il pose une
         carte blanche, une bordure et 15 px de texte. Sur un mur, le message de fin
         s'affichait donc en petit dans un rectangle blanc au milieu de l'écran. Le
         contenu est le même HTML, l'habillage est celui de la salle. */
      /* ⚠️ LE MÊME BLOC NOMMÉ QUE L'ATTENTE : c'est le même habillage, et une règle
         posée sur l'un (le fond, la police) vaut pour l'autre. */
      <div className="stage" data-cfg="sceneStage">
        <Prose
          html={afterHtml}
          className="scene__end"
          />
      </div>
    );
  }

  if (live?.activity && live.state.broadcast === "live") {
    return (
      /**
       * ⚠️ LE MÊME MODULE QUE LE TÉLÉPHONE, en grand et sans ses saisies. La salle
       * et le direct des participants montrent LA MÊME CHOSE : seule la
       * présentation change, et la vidéo saute — les gens sont dans la salle, ils
       * n'ont pas besoin qu'on la leur diffuse.
       */
      /* ⚠️ SON PROPRE BLOC NOMMÉ, DISTINCT DE `liveModule`. Le téléphone rend le même
         module : ancrée ici, une règle demandée sur l'écran de salle reste sur l'écran de
         salle, au lieu de grossir aussi chaque téléphone. */
      <FitToRoom>
        <Activity live={live} room screenToken={token} />
      </FitToRoom>
    );
  }

  /**
   * ⚠️ PLUS DE QR CODE, UN BLOC HTML À LA PLACE (tâche #374). Le QR code et l'adresse
   * du direct, ajoutés pour le bug #262, étaient imposés : l'attente — avant le direct,
   * et pendant tant que la régie n'a rien lancé — les affichait sur chaque mur. Leur
   * place est maintenant un réglage HTML que l'organisateur remplit comme il veut
   * (l'adresse, son propre QR code en image, un programme).
   *
   * ⚠️ ET PLUS RIEN D'IMPOSÉ AUTOUR (tâche #453). L'accroche « Rejoignez la session » et
   * le nom de l'événement restaient au-dessus du bloc, en très grand, que l'organisateur
   * le veuille ou non. Ce que la salle voit entre deux activités est entièrement ce HTML.
   * Vide : le fond et les bandeaux, rien d'autre.
   */
  return (
    /* Un bloc nommé, comme ceux du site : la carte au survol s'y ouvre, et les règles
       d'apparence s'y ancrent — voir `settings/cssSelector.ts`. Vide, il occupe quand même
       tout le mur (`.scene .stage` dans `live.css`) : c'est là que l'organisateur vise pour
       le remplir. */
    <div className="stage" data-cfg="sceneStage">
      {waitingHtml.trim() !== "" && (
        <Prose html={waitingHtml} className="stage__html" />
      )}
    </div>
  );
}

/**
 * ═══ LE MODULE DU MUR, RÉDUIT S'IL NE TIENT PAS ═══
 *
 * Voir `fitScale` : on mesure la hauteur naturelle du module et la place que la scène lui
 * laisse — celle de `.scene__room`, moins les marges de la page —, et on le réduit d'un
 * bloc s'il dépasse.
 *
 * ⚠️ `scale` PLUS UNE MARGE NÉGATIVE, PAS `zoom`. Réduit par `zoom`, le texte se
 * réinstalle dans une largeur relative plus grande, passe sur moins de lignes, la hauteur
 * change, et la mesure suivante corrige la précédente : le module tremblait. `scale` ne
 * touche pas à la mise en page, donc `offsetHeight` reste la hauteur naturelle, stable ;
 * la marge négative rend à la mise en page la place que la réduction libère, pour que la
 * scène centre la hauteur RÉDUITE et ne déborde plus.
 */
function FitToRoom({ children }: { children: ReactNode }) {
  const box = useContext(RoomBox);
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [measure, setMeasure] = useState({ natural: 0, padding: 0 });

  useEffect(() => {
    if (!node) return;
    const read = () => {
      const page = node.parentElement;
      const style = page ? getComputedStyle(page) : null;
      const padding = style
        ? parseFloat(style.paddingTop) + parseFloat(style.paddingBottom)
        : 0;
      const natural = node.offsetHeight;
      setMeasure((was) =>
        was.natural === natural && was.padding === padding ? was : { natural, padding },
      );
    };
    read();
    const watch = new ResizeObserver(read);
    watch.observe(node);
    return () => watch.disconnect();
  }, [node, box]);

  const scale = fitScale(measure.natural, box ? box.height - measure.padding : 0);

  return (
    <div
      ref={setNode}
      className="scene__mod"
      data-cfg="sceneModule"
      style={
        scale < 1
          ? ({
              scale: String(scale),
              transformOrigin: "top center",
              marginBottom: `${(scale - 1) * measure.natural}px`,
            } as CSSProperties)
          : undefined
      }
    >
      {children}
    </div>
  );
}
